import {
  AdminOrganizationListResponseV1Schema,
  AdminOrganizationRecoveryCandidatesResponseV1Schema,
  AdminOrganizationStatusResponseV1Schema,
  AdminPendingInviteLinkResponseV1Schema,
  OwnershipCommandResponseV1Schema,
  ProvisionOrganizationResponseV1Schema,
  type AdminOrganizationRecoveryCandidateV1,
  type AdminOrganizationSummaryV1,
} from "@fulcrum5/suite-contracts";
import {
  Building2,
  Check,
  CircleAlert,
  Copy,
  LoaderCircle,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  UserRoundCheck,
  X,
} from "lucide-react";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { PlatformApiError } from "../api/platformApi.ts";
import { usePlatformSession } from "../session/PlatformSessionProvider.tsx";

type AdminFilterStatus = "" | "provisioning" | "active" | "suspended";

interface AdminFilter {
  readonly search: string;
  readonly status: AdminFilterStatus;
}

interface PageError {
  readonly message: string;
  readonly retryable: boolean;
  readonly fieldErrors: Readonly<Record<string, string>> | undefined;
}

interface RecoveryState {
  readonly organization: AdminOrganizationSummaryV1;
  readonly phase: "loading" | "ready" | "error";
  readonly candidates: readonly AdminOrganizationRecoveryCandidateV1[];
  readonly selectedMembershipId: string | null;
  readonly error: PageError | null;
  readonly submitting: boolean;
}

function createIdempotencyKey(scope: string): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return `${scope}-${uuid ?? `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`}`;
}

function pageError(error: unknown): PageError {
  if (error instanceof PlatformApiError) {
    return {
      message: error.message,
      retryable: error.retryable,
      fieldErrors: error.error.fieldErrors,
    };
  }
  return {
    message: "Platform could not complete this request",
    retryable: true,
    fieldErrors: undefined,
  };
}

function statusLabel(status: AdminOrganizationSummaryV1["status"]): string {
  return status.slice(0, 1).toUpperCase() + status.slice(1);
}

function ownershipLabel(ownership: AdminOrganizationSummaryV1["ownershipHealth"]): string {
  return ownership === "healthy" ? "Owner confirmed" : ownership === "missing" ? "Owner missing" : "Multiple owners";
}

function billingLabel(status: AdminOrganizationSummaryV1["billingAccessStatus"]): string {
  return status === "entitled" ? "Active" : status === "grace" ? "Grace period" : status === "blocked" ? "Blocked" : "Not available";
}

function buildListPath(filter: AdminFilter, after: string | null = null): string {
  const params = new URLSearchParams();
  if (filter.search) params.set("search", filter.search);
  if (filter.status) params.set("status", filter.status);
  if (after) params.set("after", after);
  const serialized = params.toString();
  return serialized ? `/api/admin/organizations?${serialized}` : "/api/admin/organizations";
}

function ErrorNotice({ error, onRetry }: { readonly error: PageError; readonly onRetry?: () => void }): JSX.Element {
  return (
    <div className="platform-notice platform-notice--error" role="alert">
      <CircleAlert aria-hidden="true" size={18} strokeWidth={1.8} />
      <div>
        <strong>Request could not be completed</strong>
        <p>{error.message}</p>
      </div>
      {onRetry && error.retryable ? (
        <button className="platform-button platform-button--quiet" onClick={onRetry} type="button">
          <RefreshCw aria-hidden="true" size={16} strokeWidth={1.8} />
          Retry
        </button>
      ) : null}
    </div>
  );
}

function RecoveryDialog({
  recovery,
  onDismiss,
  onSelect,
  onSubmit,
  onRetry,
}: {
  readonly recovery: RecoveryState;
  onDismiss(): void;
  onSelect(membershipId: string): void;
  onSubmit(): void;
  onRetry(): void;
}): JSX.Element {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const initial = dialog.querySelector<HTMLElement>("[data-dialog-initial-focus]")
      ?? dialog.querySelector<HTMLElement>("button:not([disabled])");
    initial?.focus();
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape" && !recovery.submitting) {
        event.preventDefault();
        onDismiss();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...dialog.querySelectorAll<HTMLElement>(
        "button:not([disabled]), input:not([disabled]), [href], select:not([disabled]), textarea:not([disabled])",
      )].filter((item) => item.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    dialog.addEventListener("keydown", onKeyDown);
    return () => dialog.removeEventListener("keydown", onKeyDown);
  }, [onDismiss, recovery.phase, recovery.submitting]);

  const canSubmit = recovery.phase === "ready" && recovery.selectedMembershipId !== null && !recovery.submitting;
  return (
    <div className="platform-dialog-backdrop" role="presentation">
      <div aria-labelledby={titleId} aria-modal="true" className="platform-dialog" ref={dialogRef} role="dialog">
        <div className="platform-dialog__header">
          <div>
            <p className="platform-page__eyebrow">Fulcrum5 administration</p>
            <h2 id={titleId}>Recover organization owner</h2>
          </div>
          <button
            aria-label="Close owner recovery dialog"
            className="platform-icon-button"
            disabled={recovery.submitting}
            onClick={onDismiss}
            title="Close"
            type="button"
          >
            <X aria-hidden="true" size={18} strokeWidth={1.8} />
          </button>
        </div>
        <p className="platform-dialog__copy">
          Select one current active member to restore a single owner for {recovery.organization.name}.
        </p>

        {recovery.phase === "loading" ? (
          <div className="platform-loading" role="status">
            <LoaderCircle aria-hidden="true" className="platform-spin" size={20} strokeWidth={1.8} />
            Checking eligible members
          </div>
        ) : null}
        {recovery.phase === "error" && recovery.error ? (
          <ErrorNotice error={recovery.error} onRetry={onRetry} />
        ) : null}
        {recovery.phase === "ready" && recovery.candidates.length === 0 ? (
          <div className="platform-empty-state">
            <ShieldAlert aria-hidden="true" size={20} strokeWidth={1.8} />
            <p>No eligible active member is currently available for owner recovery.</p>
          </div>
        ) : null}
        {recovery.phase === "ready" && recovery.candidates.length > 0 ? (
          <fieldset className="recovery-candidates">
            <legend>Eligible members</legend>
            {recovery.candidates.map((candidate, index) => (
              <label className="recovery-candidates__item" key={candidate.id}>
                <input
                  checked={recovery.selectedMembershipId === candidate.id}
                  data-dialog-initial-focus={index === 0 || undefined}
                  name="recovery-candidate"
                  onChange={() => onSelect(candidate.id)}
                  type="radio"
                  value={candidate.id}
                />
                <span className="recovery-candidates__identity">
                  <strong>{candidate.displayName}</strong>
                  <span>{candidate.email ?? "No email available"}</span>
                </span>
                <span className="platform-badge" data-tone={candidate.role}>{candidate.role}</span>
              </label>
            ))}
          </fieldset>
        ) : null}
        {recovery.phase === "ready" && recovery.error ? <ErrorNotice error={recovery.error} /> : null}
        <div className="platform-dialog__actions">
          <button className="platform-button platform-button--secondary" disabled={recovery.submitting} onClick={onDismiss} type="button">
            Cancel
          </button>
          <button className="platform-button" disabled={!canSubmit} onClick={onSubmit} type="button">
            {recovery.submitting ? <LoaderCircle aria-hidden="true" className="platform-spin" size={16} strokeWidth={1.8} /> : <UserRoundCheck aria-hidden="true" size={16} strokeWidth={1.8} />}
            Restore owner
          </button>
        </div>
      </div>
    </div>
  );
}

/** Cross-organization staff workspace. It never receives or emits product-session identities. */
export function AdminOrganizationsPage(): JSX.Element {
  const { api, identity } = usePlatformSession();
  const isF5Admin = identity?.isF5Admin === true;
  const [searchDraft, setSearchDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState<AdminFilterStatus>("");
  const [filter, setFilter] = useState<AdminFilter>({ search: "", status: "" });
  const [items, setItems] = useState<readonly AdminOrganizationSummaryV1[]>([]);
  const [nextAfter, setNextAfter] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<PageError | null>(null);
  const [busyOrganizationId, setBusyOrganizationId] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<PageError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [provisionName, setProvisionName] = useState("");
  const [provisionOwnerEmail, setProvisionOwnerEmail] = useState("");
  const [provisionError, setProvisionError] = useState<PageError | null>(null);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [recovery, setRecovery] = useState<RecoveryState | null>(null);
  const listRequestVersion = useRef(0);
  const listRequestAbortController = useRef<AbortController | null>(null);
  const recoveryOpenerRef = useRef<HTMLButtonElement | null>(null);
  const recoveryRequestVersion = useRef(0);

  const loadOrganizations = useCallback(async (
    nextFilter: AdminFilter,
    after: string | null = null,
    append = false,
  ) => {
    listRequestAbortController.current?.abort();
    const controller = new AbortController();
    listRequestAbortController.current = controller;
    const requestVersion = listRequestVersion.current + 1;
    listRequestVersion.current = requestVersion;
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await api.request({
        path: buildListPath(nextFilter, after),
        responseSchema: AdminOrganizationListResponseV1Schema,
        signal: controller.signal,
      });
      if (controller.signal.aborted || listRequestVersion.current !== requestVersion) return;
      setItems((current) => append ? [...current, ...response.data.items] : response.data.items);
      setNextAfter(response.data.nextAfter);
    } catch (cause) {
      if (controller.signal.aborted || listRequestVersion.current !== requestVersion) return;
      setLoadError(pageError(cause));
    } finally {
      if (listRequestVersion.current !== requestVersion) return;
      if (listRequestAbortController.current === controller) listRequestAbortController.current = null;
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => () => {
    listRequestVersion.current += 1;
    listRequestAbortController.current?.abort();
    listRequestAbortController.current = null;
  }, []);

  useEffect(() => {
    if (!isF5Admin) return;
    void loadOrganizations({ search: "", status: "" });
  }, [isF5Admin, loadOrganizations]);

  const submitSearch = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextFilter = { search: searchDraft.trim(), status: statusDraft } as const;
    setFilter(nextFilter);
    void loadOrganizations(nextFilter);
  }, [loadOrganizations, searchDraft, statusDraft]);

  const provision = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = provisionName.trim();
    const ownerEmail = provisionOwnerEmail.trim();
    if (!name || !ownerEmail || isProvisioning) return;
    setProvisionError(null);
    setMutationError(null);
    setNotice(null);
    setIsProvisioning(true);
    try {
      await api.request({
        path: "/api/admin/organizations",
        method: "POST",
        body: { version: 1, name, ownerEmail },
        headers: { "idempotency-key": createIdempotencyKey("provision-organization") },
        responseSchema: ProvisionOrganizationResponseV1Schema,
      });
      setProvisionName("");
      setProvisionOwnerEmail("");
      setProvisionOpen(false);
      setNotice("Provisioning started.");
      await loadOrganizations(filter);
    } catch (cause) {
      setProvisionError(pageError(cause));
    } finally {
      setIsProvisioning(false);
    }
  }, [api, filter, isProvisioning, loadOrganizations, provisionName, provisionOwnerEmail]);

  const runLifecycleAction = useCallback(async (
    organization: AdminOrganizationSummaryV1,
    action: "retry" | "suspend" | "restore",
  ) => {
    setBusyOrganizationId(organization.id);
    setMutationError(null);
    setNotice(null);
    try {
      if (action === "retry") {
        await api.request({
          path: `/api/admin/organizations/${organization.id}/retry-provisioning`,
          method: "POST",
          body: { version: 1 },
          responseSchema: ProvisionOrganizationResponseV1Schema,
        });
        setNotice("Provisioning retry requested.");
      } else {
        await api.request({
          path: `/api/admin/organizations/${organization.id}/${action}`,
          method: "POST",
          body: { version: 1 },
          responseSchema: AdminOrganizationStatusResponseV1Schema,
        });
        setNotice(action === "suspend" ? "Organization suspended." : "Organization restored.");
      }
      await loadOrganizations(filter);
    } catch (cause) {
      setMutationError(pageError(cause));
    } finally {
      setBusyOrganizationId(null);
    }
  }, [api, filter, loadOrganizations]);

  const copyPendingInviteLink = useCallback(async (organization: AdminOrganizationSummaryV1) => {
    setBusyOrganizationId(organization.id);
    setMutationError(null);
    setNotice(null);
    try {
      const result = await api.request({
        path: `/api/admin/organizations/${organization.id}/pending-invite-link`,
        responseSchema: AdminPendingInviteLinkResponseV1Schema,
      });
      const link = result.data.inviteLink;
      try {
        await navigator.clipboard.writeText(link);
        setNotice(`Invite link copied for ${result.data.email} (${result.data.role}).`);
      } catch {
        // Clipboard can fail without secure context / permission — surface the URL.
        setNotice(`Invite link for ${result.data.email}: ${link}`);
      }
    } catch (cause) {
      setMutationError(pageError(cause));
    } finally {
      setBusyOrganizationId(null);
    }
  }, [api]);

  const dismissRecovery = useCallback(() => {
    recoveryRequestVersion.current += 1;
    setBusyOrganizationId(null);
    setRecovery(null);
    window.setTimeout(() => recoveryOpenerRef.current?.focus(), 0);
  }, []);

  const fetchRecoveryCandidates = useCallback(async (
    organization: AdminOrganizationSummaryV1,
    opener?: HTMLButtonElement | null,
  ) => {
    const requestVersion = recoveryRequestVersion.current + 1;
    recoveryRequestVersion.current = requestVersion;
    if (opener !== undefined) recoveryOpenerRef.current = opener;
    setBusyOrganizationId(organization.id);
    setRecovery({
      organization,
      phase: "loading",
      candidates: [],
      selectedMembershipId: null,
      error: null,
      submitting: false,
    });
    try {
      const response = await api.request({
        path: `/api/admin/organizations/${organization.id}/recovery-candidates?limit=100`,
        responseSchema: AdminOrganizationRecoveryCandidatesResponseV1Schema,
      });
      if (recoveryRequestVersion.current !== requestVersion) return;
      setRecovery({
        organization,
        phase: "ready",
        candidates: response.data.candidates,
        selectedMembershipId: null,
        error: null,
        submitting: false,
      });
    } catch (cause) {
      if (recoveryRequestVersion.current !== requestVersion) return;
      setRecovery({
        organization,
        phase: "error",
        candidates: [],
        selectedMembershipId: null,
        error: pageError(cause),
        submitting: false,
      });
    } finally {
      if (recoveryRequestVersion.current === requestVersion) setBusyOrganizationId(null);
    }
  }, [api]);

  const restoreOwner = useCallback(async () => {
    if (!recovery || recovery.phase !== "ready" || !recovery.selectedMembershipId || recovery.submitting) return;
    const activeRecovery = recovery;
    setRecovery({ ...activeRecovery, submitting: true, error: null });
    setBusyOrganizationId(activeRecovery.organization.id);
    setNotice(null);
    try {
      await api.request({
        path: `/api/admin/organizations/${activeRecovery.organization.id}/recover-owner`,
        method: "POST",
        body: { version: 1, selectedMembershipId: activeRecovery.selectedMembershipId },
        headers: { "idempotency-key": createIdempotencyKey("recover-organization-owner") },
        responseSchema: OwnershipCommandResponseV1Schema,
      });
      await loadOrganizations(filter);
      dismissRecovery();
      setNotice("Owner recovery completed.");
    } catch (cause) {
      setRecovery((current) => current ? { ...current, error: pageError(cause), submitting: false } : current);
    } finally {
      setBusyOrganizationId(null);
    }
  }, [api, dismissRecovery, filter, loadOrganizations, recovery]);

  if (!isF5Admin) {
    return (
      <section className="platform-page admin-organizations-page" aria-labelledby="admin-organizations-title">
        <p className="platform-page__eyebrow">Fulcrum5 administration</p>
        <h1 id="admin-organizations-title">Client organizations</h1>
        <p className="platform-admins-page__denied" role="alert">This workspace is only available to Fulcrum5 administrators.</p>
      </section>
    );
  }

  const provisionNameError = provisionError?.fieldErrors?.name ?? provisionError?.fieldErrors?.body;
  const provisionEmailError = provisionError?.fieldErrors?.ownerEmail;

  return (
    <section className="platform-page admin-organizations-page" aria-labelledby="admin-organizations-title">
      <header className="platform-page__header">
        <div>
          <p className="platform-page__eyebrow">Fulcrum5 administration</p>
          <h1 id="admin-organizations-title">Client organizations</h1>
          <p className="platform-page__summary">Provision and maintain organization access across the Fulcrum5 suite.</p>
        </div>
        <button className="platform-button" onClick={() => setProvisionOpen((value) => !value)} type="button">
          <Plus aria-hidden="true" size={16} strokeWidth={1.8} />
          Provision organization
        </button>
      </header>

      {provisionOpen ? (
        <form className="admin-provision-form" onSubmit={provision} noValidate>
          <label className="platform-field" htmlFor="provision-organization-name">
            <span>Organization name</span>
            <input
              aria-invalid={provisionNameError ? "true" : undefined}
              id="provision-organization-name"
              onChange={(event) => setProvisionName(event.target.value)}
              value={provisionName}
            />
            {provisionNameError ? <span className="platform-field__error" role="alert">{provisionNameError}</span> : null}
          </label>
          <label className="platform-field" htmlFor="provision-owner-email">
            <span>Owner email</span>
            <input
              aria-invalid={provisionEmailError ? "true" : undefined}
              id="provision-owner-email"
              onChange={(event) => setProvisionOwnerEmail(event.target.value)}
              type="email"
              value={provisionOwnerEmail}
            />
            {provisionEmailError ? <span className="platform-field__error" role="alert">{provisionEmailError}</span> : null}
          </label>
          <div className="admin-provision-form__actions">
            <button className="platform-button platform-button--secondary" disabled={isProvisioning} onClick={() => setProvisionOpen(false)} type="button">Cancel</button>
            <button className="platform-button" disabled={isProvisioning || !provisionName.trim() || !provisionOwnerEmail.trim()} type="submit">
              {isProvisioning ? <LoaderCircle aria-hidden="true" className="platform-spin" size={16} strokeWidth={1.8} /> : <Building2 aria-hidden="true" size={16} strokeWidth={1.8} />}
              Start provisioning
            </button>
          </div>
          {provisionError && !provisionNameError && !provisionEmailError ? <ErrorNotice error={provisionError} /> : null}
        </form>
      ) : null}

      <form className="admin-organization-filters" onSubmit={submitSearch}>
        <label className="platform-field" htmlFor="organization-search">
          <span className="platform-visually-hidden">Search organizations</span>
          <input id="organization-search" onChange={(event) => setSearchDraft(event.target.value)} placeholder="Search organizations" value={searchDraft} />
        </label>
        <label className="platform-field" htmlFor="organization-status">
          <span className="platform-visually-hidden">Organization status</span>
          <select id="organization-status" onChange={(event) => setStatusDraft(event.target.value as AdminFilterStatus)} value={statusDraft}>
            <option value="">All statuses</option>
            <option value="provisioning">Provisioning</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </label>
        <button className="platform-button platform-button--secondary" type="submit">
          <Search aria-hidden="true" size={16} strokeWidth={1.8} />
          Search
        </button>
      </form>

      {notice ? <div className="platform-notice platform-notice--success" role="status"><Check aria-hidden="true" size={18} strokeWidth={1.8} />{notice}</div> : null}
      {mutationError ? <ErrorNotice error={mutationError} /> : null}
      {loadError ? <ErrorNotice error={loadError} onRetry={() => { void loadOrganizations(filter); }} /> : null}
      {isLoading && items.length === 0 ? <div className="platform-loading" role="status"><LoaderCircle aria-hidden="true" className="platform-spin" size={20} /> Loading organizations</div> : null}
      {!isLoading && !loadError && items.length === 0 ? (
        <div className="platform-empty-state"><Building2 aria-hidden="true" size={22} strokeWidth={1.8} /><p>No client organizations match this view.</p></div>
      ) : null}

      {items.length > 0 ? (
        <section className="admin-organization-list" aria-label="Client organizations" aria-busy={isLoading || undefined}>
          {items.map((organization) => {
            const busy = busyOrganizationId === organization.id;
            const needsRecovery = organization.ownershipHealth !== "healthy";
            return (
              <article className="admin-organization-row" key={organization.id} aria-busy={busy || undefined}>
                <div className="admin-organization-row__identity">
                  <h2>{organization.name}</h2>
                  <dl className="admin-organization-row__metadata">
                    <div><dt>Lifecycle</dt><dd><span className="platform-badge" data-tone={organization.status}>{statusLabel(organization.status)}</span></dd></div>
                    <div><dt>Ownership</dt><dd><span className="platform-badge" data-tone={organization.ownershipHealth}>{ownershipLabel(organization.ownershipHealth)}</span></dd></div>
                    <div><dt>Suite access</dt><dd>{billingLabel(organization.billingAccessStatus)}</dd></div>
                    <div><dt>Created</dt><dd>{new Date(organization.createdAt).toLocaleDateString()}</dd></div>
                  </dl>
                </div>
                <div className="admin-organization-row__actions">
                  {organization.status === "provisioning" ? (
                    <button className="platform-button platform-button--secondary" disabled={busy} onClick={() => { void runLifecycleAction(organization, "retry"); }} type="button">
                      {busy ? <LoaderCircle aria-hidden="true" className="platform-spin" size={16} strokeWidth={1.8} /> : <RefreshCw aria-hidden="true" size={16} strokeWidth={1.8} />}
                      Retry provisioning
                    </button>
                  ) : null}
                  {organization.status === "provisioning" || organization.status === "active" ? (
                    <button
                      aria-label={`Copy pending invite link for ${organization.name}`}
                      className="platform-button platform-button--secondary"
                      disabled={busy}
                      onClick={() => { void copyPendingInviteLink(organization); }}
                      type="button"
                    >
                      {busy ? <LoaderCircle aria-hidden="true" className="platform-spin" size={16} strokeWidth={1.8} /> : <Copy aria-hidden="true" size={16} strokeWidth={1.8} />}
                      Copy invite link
                    </button>
                  ) : null}
                  {organization.status === "active" ? (
                    <button aria-label={`Suspend ${organization.name}`} className="platform-button platform-button--secondary" disabled={busy} onClick={() => { void runLifecycleAction(organization, "suspend"); }} type="button">
                      {busy ? <LoaderCircle aria-hidden="true" className="platform-spin" size={16} strokeWidth={1.8} /> : <ShieldAlert aria-hidden="true" size={16} strokeWidth={1.8} />}
                      Suspend
                    </button>
                  ) : null}
                  {organization.status === "suspended" ? (
                    <button className="platform-button platform-button--secondary" disabled={busy} onClick={() => { void runLifecycleAction(organization, "restore"); }} type="button">
                      {busy ? <LoaderCircle aria-hidden="true" className="platform-spin" size={16} strokeWidth={1.8} /> : <ShieldCheck aria-hidden="true" size={16} strokeWidth={1.8} />}
                      Restore
                    </button>
                  ) : null}
                  {needsRecovery ? (
                    <button
                      aria-label={`Recover owner for ${organization.name}`}
                      className="platform-button platform-button--secondary"
                      disabled={busy}
                      onClick={(event) => { void fetchRecoveryCandidates(organization, event.currentTarget); }}
                      type="button"
                    >
                      <UserRoundCheck aria-hidden="true" size={16} strokeWidth={1.8} />
                      Recover owner
                    </button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </section>
      ) : null}

      {nextAfter ? (
        <button className="platform-button platform-button--secondary admin-organization-load-more" disabled={isLoading} onClick={() => { void loadOrganizations(filter, nextAfter, true); }} type="button">
          {isLoading ? <LoaderCircle aria-hidden="true" className="platform-spin" size={16} strokeWidth={1.8} /> : null}
          Load more organizations
        </button>
      ) : null}

      {recovery ? (
        <RecoveryDialog
          onDismiss={dismissRecovery}
          onRetry={() => { void fetchRecoveryCandidates(recovery.organization); }}
          onSelect={(selectedMembershipId) => setRecovery((current) => current ? { ...current, selectedMembershipId, error: null } : current)}
          onSubmit={() => { void restoreOwner(); }}
          recovery={recovery}
        />
      ) : null}
    </section>
  );
}
